import {
  BOX_CATEGORY_INFO,
  type BoxCategory,
  type CatalogCopy,
  type StorefrontTerm,
} from "@loot-os/api-contract";
import type {
  BoxGridContent,
  BoxGridState,
  BoxTab,
} from "@loot-os/design-system/templates/boxes/boxesContent";
import type { BoxCardContent } from "@loot-os/design-system/templates/shared/content";
import { useQuery } from "@tanstack/react-query";
import { spendableCents } from "../../../../lib/balance.js";
import { useCurrency } from "../../../../lib/currency.js";
import { useFavorites } from "../../../../lib/favorites.js";
import { boxesQueryOptions } from "../../../../lib/queries.js";
import { useBalance } from "../../../../lib/shopper.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { useBoxCards } from "../../shared/boxCards.js";
import { useActiveBoxCategory } from "./categories.js";
import { useCatalogCopy } from "./copy.js";
import { filterBoxes, NEW_BOX_DAYS } from "./filterBoxes.js";
import { type BoxesSearch, useBoxesSearch } from "./search.js";

/** The grid: GET /v1/boxes through the tab, the category and every filter, each box with its heart. */
export function useBoxGridContent(): BoxGridContent {
  const copy = useCatalogCopy().grid;
  const { box } = useTerminology();
  const currency = useCurrency();
  const [search] = useBoxesSearch();
  const category = useActiveBoxCategory();
  const query = useQuery(boxesQueryOptions());
  const favorites = useFavorites();
  const balance = useBalance().data;
  const tab = search.tab ?? "all";

  const shown = query.data
    ? filterBoxes(query.data, {
        tab,
        category,
        query: search.q ?? "",
        minCents: search.min ?? null,
        maxCents: search.max ?? null,
        budgetCents: search.enough && balance ? spendableCents(balance) : null,
        favorites: favorites.saved,
        sort: search.sort ?? "default",
        now: Date.now(),
      })
    : null;
  const cards = useBoxCards(shown);

  return {
    title: category ? copy.titleIn[tab](BOX_CATEGORY_INFO[category].label) : copy.title[tab],
    currency,
    state: gridState({
      empty: copy.empty,
      box,
      tab,
      category,
      search,
      error: query.error,
      listed: query.data?.length ?? null,
      cards,
      signedIn: favorites.signedIn,
      favoritesLoaded: favorites.loaded,
    }),
  };
}

function gridState(args: {
  empty: CatalogCopy["grid"]["empty"];
  /** Names the boxes in the load failure's fallback, a platform word. */
  box: StorefrontTerm;
  tab: BoxTab;
  category: BoxCategory | null;
  search: BoxesSearch;
  error: Error | null;
  /** How many boxes the environment lists; null until they load. */
  listed: number | null;
  cards: readonly BoxCardContent[] | null;
  signedIn: boolean;
  favoritesLoaded: boolean;
}): BoxGridState {
  const { empty, box, tab, search, cards } = args;
  if (args.error && args.listed === null) {
    return { status: "error", message: args.error.message || `failed to load ${box.plural}` };
  }
  if (tab === "favorites" && !args.signedIn) {
    return {
      status: "empty",
      message: empty.signIn.message,
      action: { label: empty.signIn.action, href: "/login", emphasis: "primary" },
    };
  }
  // Only the favorites tab waits for the favorites; elsewhere the hearts fill in when they land.
  if (!cards || (tab === "favorites" && !args.favoritesLoaded)) return { status: "loading" };
  if (cards.length > 0) return { status: "ready", cards };
  const filtered =
    Boolean(search.q?.trim()) ||
    search.min !== undefined ||
    search.max !== undefined ||
    search.enough === true;
  const message =
    args.listed === 0
      ? empty.noBoxes
      : args.category
        ? categoryEmptyMessage(
            empty.inCategory,
            tab,
            BOX_CATEGORY_INFO[args.category].label,
            filtered,
          )
        : filtered
          ? empty.noMatches
          : tab === "favorites"
            ? empty.noFavorites
            : empty.noNew(NEW_BOX_DAYS);
  return { status: "empty", message, action: null };
}

/** Why a category shows nothing: the filters, the tab, or nothing in it at all. */
function categoryEmptyMessage(
  empty: CatalogCopy["grid"]["empty"]["inCategory"],
  tab: BoxTab,
  label: string,
  filtered: boolean,
): string {
  if (filtered) return empty.noMatches(label);
  switch (tab) {
    case "all":
      return empty.noBoxes(label);
    case "new":
      return empty.noNew(label, NEW_BOX_DAYS);
    case "favorites":
      return empty.noFavorites(label);
  }
}
